import moment from "moment";
import {
  fastingDayEmptyMeal,
  getRoundedValue,
} from "../../../../helpers/mealsHelper";
import { DateFormat } from "../../../../modules/constants";
import { getDates } from "../../../../modules/utils";

export const headersDailyAverages = {
  date: " ",
  iscomplete: "Complete day",
  fastingDay: "Fasting day",
  calories: "Calories (kcal)",
  fats: "Total Fat (g)",
  longChainFat: "Long chain fat (g)",
  medChainFat: "Medium chain fat (g)",
  protein: "Protein (g)",
  carbohydrates: "Carbohydrates (g)",
  fiber: "Fiber (g)",
  linoleic: "Linoleic Acid (g)",
  linolenic: "Linolenic Acid (g)",
  ala: "ALA (g)",
  dha: "DHA (g)",
  vitA: "Vitamin A (IU)",
  vitD: "Vitamin D (IU)",
  vitE: "Vitamin E (mg)",
  vitEiU: "Vitamin E (IU)",
  vitK: "Vitamin K (mcg)",
  iron: "Iron (mg)",
  zinc: "Zinc (mg)",
  calcium: "Calcium (mg)",
  potassium: "Potassium (mg)",
  sodium: "Sodium (mg)",
  totalSugar: "Sugars - total (g)",
  addedSugars: "Sugars - added (g)",
  choline: "Choline - total (mg)",
  copper: "Copper (mg)",
  folic: "Folic Acid (mcg)",
  magnesium: "Magnesium (mg)",
  manganese: "Manganese (mg)",
  niacin: "Niacin (mg)",
  phosphorus: "Phosphorus (mg)",
  riboflavin: "Riboflavin (mg)",
  selenium: "Selenium (mcg)",
  thiamin: "Thiamin (mg)",
  vitB12: "Vitamin B-12 (mcg)",
  vitB6: "Vitamin B-6 (mg)",
  vitC: "Vitamin C - total ascorbic acid (mg)",
  lCarnitine: "L-Carnitine (mg)",
};

const matchParams = {
  calories: "Calories",
  totalFat: "Fats",
  longChainFat: "LongChainFats",
  medChainFat: "MediumChainFats",
  protein: "Protein",
  carbohydrates: "Carbs",
  fiber: "Fiber",
  linoleic: "LinoleicAcid",
  linolenic: "LinolenicAcid",
  ala: "ALA",
  dha: "DHA",
  vitA: "VitaminA",
  vitD: "VitaminD",
  vitE: "VitaminE",
  vitEiU: "VitaminE_UI",
  vitK: "VitaminK",
  iron: "Iron",
  zinc: "Zinc",
  calcium: "Calcium",
  potassium: "Potassium",
  sodium: "Sodium",
  totalSugar: "SugarsTotal",
  addedSugars: "SugarsAdded",
  choline: "CholineTotal",
  copper: "Copper",
  folic: "FolicAcid",
  magnesium: "Magnesium",
  manganese: "Manganese",
  niacin: "Niacin",
  phosphorus: "Phosphorus",
  riboflavin: "Riboflavin",
  selenium: "Selenium",
  thiamin: "Thiamin",
  vitB12: "VitaminB12",
  vitB6: "VitaminB6",
  vitC: "VitaminC_TotalAscorbicAcid",
  lCarnitine: "L_Carnitine",
};

const getObjectWithMatchedKeys = (item, previousDate, meal) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    newObj[key] = item.completed
      ? getRoundedValue(
          Number(previousDate[key] ?? 0) +
            Number(meal[matchParams[key]]?.value ?? 0)
        )
      : "";
  });
  return newObj;
};

export function getDataToDailyAveragesReport(rawData, startDate, endDate) {
  const formatattedData = Object.values(
    rawData.entries.total.reduce(aggregatorByDay, {})
  );

  formatattedData
    .filter((meal) => meal.completed === "Y")
    .forEach((meal) => {
      Object.keys(matchParams).forEach((key) => {
        meal[key] = getRoundedValue(meal[key]);
      });
    });

  const finalData = [];

  finalData.push(getAverage(formatattedData, startDate, endDate));

  const dateRange = getDates(new Date(startDate), new Date(endDate));
  dateRange.forEach((date) => {
    const meal = formatattedData.find((item) => item.date === date);
    if (meal) {
      finalData.push(meal);
    } else {
      finalData.push({
        date: moment(date).format(DateFormat.BASE_FORMAT),
        completed: "-",
        fastingDay: "-",
        ...prepopulateWithMatchedKeys("-"),
      });
    }
  });

  return finalData;
}

const aggregatorByDay = (accumulator, item) => {
  const meal = JSON.parse(item.mealJson).aboutMeal;

  const date = moment(item.mealDate).format(DateFormat.BASE_FORMAT).toString();
  let previousDate = accumulator[date];
  if (!previousDate) {
    previousDate = accumulator;
  }

  return {
    ...accumulator,
    [date]: {
      date: date,
      completed: item.completed || previousDate.fastingDay === "Y" ? "Y" : "N",
      fastingDay:
        item.fastingDay || item.mealJson === fastingDayEmptyMeal ? "Y" : "N",
      ...getObjectWithMatchedKeys(item, previousDate, meal),
    },
  };
};

const getAverage = (data, startDate, endDate) => {
  const initialValue = {
    date: `AVG ${moment(startDate).format(DateFormat.BASE_FORMAT)} - ${moment(
      endDate
    ).format(DateFormat.BASE_FORMAT)}`,
    completed: 0,
    fastingDay: 0,
    ...prepopulateWithMatchedKeys(0),
  };

  //Aggregate all values and calculate the average
  const avg = calculateAvg(
    data
      .filter((item) => item.completed === "Y")
      .reduce(aggregateAll, initialValue)
  );
  const finalAvg = {
    ...avg,
    completed: "",
    fastingDay: "",
    ...getRoundedValuesWithMatchedKeys(avg),
  };
  return finalAvg;
};

const getRoundedValuesWithMatchedKeys = (avg) => {
  const newObj = {};
  const paramsLength = Object.keys(matchParams).length;
  Object.keys(matchParams).forEach((key, index) => {
    newObj[key] = `${getRoundedValue(avg[key]?.value)} ${
      index === paramsLength - 1 ? "\r\n" : ""
    }`;
  });
  return newObj;
};

const getAVGWithMatchedKeys = (data, denominator) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    newObj[key] = { value: Number(data[key].value) / denominator };
  });
  return newObj;
};

//d = denominator
export const calculateAvg = (data, d) => {
  let average = {
    ...data,
  };

  const denominator = d || data.completed;

  if (denominator > 0) {
    average = {
      ...data,

      ...getAVGWithMatchedKeys(data, denominator),
    };
  }
  return average;
};

const sumWithMatchedKeys = (accumulator, item) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    newObj[key] = {
      value: Number(accumulator[key]?.value ?? 0) + Number(item[key] ?? 0),
    };
  });
  return newObj;
};

const prepopulateWithMatchedKeys = (value) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    newObj[key] = value;
  });
  return newObj;
};

const aggregateAll = (accumulator, item) => {
  return {
    ...accumulator,
    ...sumWithMatchedKeys(accumulator, item),

    completed: (accumulator.completed ?? 0) + 1,
  };
};
